import type { Binding, Layer, Layers } from "@/domain/types";

export interface ModifierState {
  shift?: boolean;
  ctrl?: boolean;
  alt?: boolean;
}

/**
 * A resolved binding with its provenance: the Binding plus the combo
 * whose modifier layer defines it ("" for base). Provenance is first-class so a
 * view can name the source combo and style a fallback key differently.
 */
export interface ResolvedBinding {
  binding: Binding;
  combo: string;
}

/** A resolved face of the Profile: each bound code carries its Binding and source combo. */
export type ResolvedLayer = Record<string, ResolvedBinding>;

const MODIFIER_ORDER = ["ctrl", "alt", "shift"] as const;

/** One of the three modifier families a Profile layer can name. */
export type ModifierFamily = (typeof MODIFIER_ORDER)[number];

/** Canonical combo key for a modifier state. "" when none held. */
export function comboKey(mods: ModifierState): string {
  return MODIFIER_ORDER.filter((m) => mods[m]).join("+");
}

/**
 * The modifier families this Profile's layers actually use, in canonical order
 * (ctrl, alt, shift). Drives the modifier-layer strip's toggles: a Profile
 * with no `ctrl+...` layer never offers a Ctrl latch.
 */
export function profileModifiers(layers: Layers): ModifierFamily[] {
  const used = new Set<string>();
  for (const combo of Object.keys(layers.mods ?? {})) {
    for (const part of normalizeCombo(combo).split("+")) {
      if (part) used.add(part);
    }
  }
  return MODIFIER_ORDER.filter((m) => used.has(m));
}

/** Normalize a combo string to the canonical key (order- and case-insensitive). */
export function normalizeCombo(key: string): string {
  const held = new Set(
    key
      .toLowerCase()
      .split("+")
      .map((s) => s.trim()),
  );
  return MODIFIER_ORDER.filter((m) => held.has(m)).join("+");
}

/** A resolved layer set as a flat list: base first, then the modifier layers. */
export function allLayers(layers: Layers): Layer[] {
  return [layers.base, ...Object.values(layers.mods ?? {})];
}

/** Count the positional Bindings across base and modifier layers. */
export function boundCount(layers: Layers): number {
  return allLayers(layers).reduce((n, l) => n + Object.keys(l).length, 0);
}

/**
 * Every layer that binds `code`, base first then the modifier layers by combo rank
 * (single mods before combos), alpha within a rank. Drives the readout's other-layer
 * chips: "this key also does X on Ctrl, Y on Alt".
 */
export function layersBindingCode(
  layers: Layers,
  code: string,
): { combo: string; binding: Binding }[] {
  const out: { combo: string; binding: Binding }[] = [];
  const base = layers.base[code];
  if (base) out.push({ combo: "", binding: base });
  for (const [combo, layer] of Object.entries(layers.mods ?? {})) {
    const b = layer[code];
    if (b) out.push({ combo: normalizeCombo(combo), binding: b });
  }
  const rank = (c: string) => (c === "" ? 0 : c.split("+").length);
  return out.sort((a, b) => rank(a.combo) - rank(b.combo) || a.combo.localeCompare(b.combo));
}

/** The Layer an exact combo string selects; "" selects base, an unmatched combo is empty. */
export function layerForCombo(layers: Layers, combo: string): Layer {
  const want = normalizeCombo(combo);
  if (!want) return { ...(layers.base ?? {}) };
  const entry = Object.entries(layers.mods ?? {}).find(([k]) => normalizeCombo(k) === want);
  return entry ? { ...entry[1] } : {};
}

/**
 * Resolve the active face of a Profile for a held modifier combo. The
 * exact-combo layer always wins; a key it leaves unbound falls back to its base
 * binding (base only, never another modifier layer). Each entry reports the combo
 * that defines it ("" for base), so a view can name the source and mark fallbacks.
 */
export function resolveActiveLayer(layers: Layers, mods: ModifierState = {}): ResolvedLayer {
  return resolveCombo(layers, comboKey(mods));
}

/** resolveActiveLayer keyed by a combo string directly (order- and case-insensitive). */
export function resolveCombo(layers: Layers, combo: string): ResolvedLayer {
  const want = normalizeCombo(combo);
  const out: ResolvedLayer = {};
  // base fills the holes first as the fallback; the exact combo overlays and wins
  if (want) {
    for (const [code, binding] of Object.entries(layers.base)) {
      out[code] = { binding, combo: "" };
    }
  }
  const exact = want ? layerForCombo(layers, want) : layers.base;
  for (const [code, binding] of Object.entries(exact)) {
    out[code] = { binding, combo: want };
  }
  return out;
}
